import { ZONA_DE_OPERACION } from '@/shared/lib/dateUtils'
import { CapaDeModal } from '@/shared/components/CapaDeModal'
import React, { useState, useEffect, useMemo } from "react";

// ─── Error → mensaje amigable ─────────────────────────────────────────────────

const VALIDATION_MAP: Record<string, string> = {
  "warehouseId must be a UUID": "Selecciona un almacén válido.",
  "warehouseId should not be empty": "El almacén es requerido.",
  "categoryId must be a UUID": "Selecciona una categoría válida.",
  "name should not be empty": "El nombre es requerido.",
  "serialCode should not be empty": "El código interno es requerido.",
  "serialCode must be unique":
    "Ya existe una herramienta con ese código o número de serie.",
};

function friendlyError(err: unknown): string {
  const e = err as { statusCode?: number; message?: string | string[] };
  if (e.statusCode === 500)
    return "Error del servidor. Inténtalo de nuevo o contacta al administrador.";
  if (e.statusCode === 409)
    return "Ya existe una herramienta con ese código o número de serie.";
  const msgs: string[] = Array.isArray(e.message)
    ? e.message
    : [e.message ?? "Error desconocido"];
  return msgs.map((m) => VALIDATION_MAP[m] ?? m).join(" ");
}
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { useFilaSaliente } from "@/shared/hooks/useFilaSaliente";
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import {
  Search,
  MoreVertical,
  Eye,
  Pencil,
  History,
  AlertTriangle,
  CirclePlus,
  Trash2,
  Ban,
  RefreshCw,
  Loader2,
  ArrowLeft,
  Save,
} from "lucide-react";
import toast from "react-hot-toast";
import HerramientaForm, {
  ReporteModal,
  DarBajaModal,
  DarAltaModal,
} from '@/features/almacen/components/HerramientaForm';
import { herramientasApi } from '@/features/almacen/api/herramientas.api';
import type { ToolResponseDto } from '@/features/almacen/api/herramientas.api';
import { MiniaturaHerramienta } from '@/features/almacen/components/MiniaturaHerramienta';
import { primeraFoto } from '@/features/almacen/lib/fotoDeHerramienta';
import { apiService } from '@/services/core/api.service';
import {
  usePermissions,
  ALMACEN_PERMISSIONS,
} from '@/shared/hooks/usePermissions';
import { ALMACEN } from '@/constants/permissions';
import { PermissionGuard } from '@/shared/components';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import SelectBuscable from '@/shared/components/SelectBuscable';
import FilterSelect from '@/shared/components/FilterSelect';
import { ResponsableDeHerramienta } from '@/features/almacen/components/ResponsableDeHerramienta'
import { useEstadoEnUrl } from '@/shared/hooks/useEstadoEnUrl';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import { Badge, type ColorDeBadge } from "@/shared/components/Badge";

// ─── Types ────────────────────────────────────────────────────────────────────

type Disponibilidad = "Disponible" | "No disponible";
type EstadoHerramienta =
  | "En buen estado"
  | "Equipo nuevo"
  | "Falla/daño"
  | "Faltante"
  | "En mantenimiento"
  | "Dada de baja";

interface Herramienta {
  id: string;
  nombre: string;
  /** Primera foto de la herramienta. Nunca una evidencia de movimiento. */
  foto: string | null;
  almacen: string;
  warehouseId: string;
  categoria: string;
  proyectoAsignado: string;
  responsable: string;
  codigoSerie: string;
  internalCode: string;
  disponibilidad: Disponibilidad;
  estado: EstadoHerramienta;
  createdAt: string;
  currentMovementId: string | null;
  /** CR-03: la asignación vigente todavía espera que el receptor acepte. */
  estadoDeAprobacion?: 'pending' | 'approved' | 'rejected' | null;
  pendienteDe?: string | null;
}

interface ApiRequisicion {
  id: string;
  requestedToolName: string;
  justification: string;
  destinationWarehouseId: string;
  destinationWarehouseName: string | null;
  requestedByUserId: string;
  requestedByUserName: string | null;
  incidentReportId: string | null;
  erpSyncStatus: "pending" | "synced" | "failed";
  erpSyncAt: string | null;
  createdAt: string;
}

// ─── Mapping helper ───────────────────────────────────────────────────────────

function mapToolDto(dto: ToolResponseDto): Herramienta {
  // Disponible = sin asignación activa y en condiciones de usarse. Antes solo
  // miraba el estado físico, así que una herramienta asignada y en buen estado
  // aparecía como Disponible. Mismo criterio que la app.
  const disponibilidad: Disponibilidad =
    dto.currentMovementId === null &&
    (dto.status === "good" || dto.status === "new_equipment")
      ? "Disponible"
      : "No disponible";

  let estado: EstadoHerramienta;
  switch (dto.status) {
    case "new_equipment":
      estado = "Equipo nuevo";
      break;
    case "damaged":
      estado = "Falla/daño";
      break;
    case "missing":
      estado = "Faltante";
      break;
    case "in_maintenance":
      estado = "En mantenimiento";
      break;
    case "decommissioned":
      estado = "Dada de baja";
      break;
    default:
      estado = "En buen estado";
  }

  return {
    id: dto.id,
    nombre: dto.name,
    foto: primeraFoto(dto),
    almacen: dto.warehouseName,
    warehouseId: dto.warehouseId,
    categoria: dto.categoryName,
    proyectoAsignado: dto.projectName ?? "Sin proyecto",
    responsable: dto.responsibleUserName ?? "Sin responsable",
    codigoSerie: dto.serialCode ?? "Sin número de serie",
    internalCode: dto.internalCode ?? "Sin código interno",
    disponibilidad,
    estado,
    createdAt: dto.createdAt,
    currentMovementId: dto.currentMovementId,
    estadoDeAprobacion: dto.currentApprovalStatus,
    pendienteDe: dto.pendingResponsibleUserName,
  };
}

// ─── Actions menu ─────────────────────────────────────────────────────────────

const ActionsMenu = ({
  onVer,
  onEditar,
  onHistorial,
  onReportarDano,
  onDarBaja,
  onDarAlta,
  onEliminar,
}: {
  onVer?: () => void;
  onEditar?: () => void;
  onHistorial?: () => void;
  onReportarDano?: () => void;
  onDarBaja?: () => void;
  onDarAlta?: () => void;
  onEliminar?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        btnRef.current &&
        !btnRef.current
          .closest("[data-actions-menu]")
          ?.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      // Altura estimada del menú: ~37px por item + padding (+ separador y acciones destructivas)
      const destructiveCount =
        (onDarBaja ? 1 : 0) + (onDarAlta ? 1 : 0) + (onEliminar ? 1 : 0);
      const itemCount = 4 + destructiveCount;
      const estHeight = itemCount * 37 + 8 + (destructiveCount ? 9 : 0);
      const spaceBelow = window.innerHeight - rect.bottom;
      // Si no cabe abajo, abrir hacia arriba (clamping al borde superior)
      const top =
        spaceBelow < estHeight + 8
          ? Math.max(8, rect.top - estHeight - 4)
          : rect.bottom + 4;
      setMenuPos({ top, left: rect.right - 176 });
    }
    setOpen((v) => !v);
  };

  return (
    <div data-actions-menu="">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <MoreVertical className="w-4 h-4" style={{ color: "#315FB9" }} />
      </button>
      {open && (
        <div
          className="fixed w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {[
            { label: "Ver", icon: Eye, action: onVer },
            { label: "Editar", icon: Pencil, action: onEditar },
            { label: "Historial", icon: History, action: onHistorial },
            {
              label: "Reportar daño",
              icon: AlertTriangle,
              action: onReportarDano,
            },
          ].map(({ label, icon: Icon, action }) => (
            <button
              key={label}
              onClick={() => {
                action?.();
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Icon className="w-4 h-4" style={{ color: "#315FB9" }} />
              {label}
            </button>
          ))}
          {(onDarBaja || onDarAlta || onEliminar) && (
            <div className="my-1 border-t border-gray-100" />
          )}
          {onDarAlta && (
            <button
              onClick={() => {
                onDarAlta();
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-green-50 transition-colors"
              style={{ color: "#1AA45A" }}
            >
              <RefreshCw className="w-4 h-4" />
              Dar de alta
            </button>
          )}
          {onDarBaja && (
            <button
              onClick={() => {
                onDarBaja();
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-orange-50 transition-colors"
              style={{ color: "#EA7C2B" }}
            >
              <Ban className="w-4 h-4" />
              Dar de baja
            </button>
          )}
          {onEliminar && (
            <button
              onClick={() => {
                onEliminar();
                setOpen(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Badges ───────────────────────────────────────────────────────────────────

const DisponibilidadBadge = ({ value }: { value: Disponibilidad }) => (
  <Badge color={value === "Disponible" ? "verde" : "rojo"}>{value}</Badge>
);

const EstadoBadge = ({ estado }: { estado: EstadoHerramienta }) => {
  const colores: Record<EstadoHerramienta, ColorDeBadge> = {
    "En buen estado": "verde",
    "Equipo nuevo": "azul",
    "Falla/daño": "rojo",
    Faltante: "amarillo",
    "En mantenimiento": "amarillo",
    "Dada de baja": "gris",
  };
  return <Badge color={colores[estado]}>{estado}</Badge>;
};

// ─── Empty / loading / error states ──────────────────────────────────────────

const NoDataState = () => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <img
      src="/herramientas-empty-state.svg"
      alt="Sin herramientas"
      className="w-40 h-auto mb-2"
    />
    <p className="text-base font-semibold text-gray-700">
      Aún no hay datos para mostrar
    </p>
    <p className="text-sm text-gray-400">
      Empieza registrando una herramienta.
    </p>
  </div>
);

const NoResultsState = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-2">
    <p className="text-sm font-medium text-gray-500">Sin resultados</p>
    <p className="text-sm text-gray-400">Intenta con otros filtros.</p>
  </div>
);

// A-01: en vez de un spinner sobre el vacio, la silueta de la tabla. La
// pantalla conserva su forma y al llegar los datos nada brinca.
const LoadingState = () => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <EsqueletoTabla filas={7} columnas={6} />
  </div>
);

const ErrorState = ({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <p className="text-base font-semibold text-red-600">
      Error al cargar herramientas
    </p>
    <p className="text-sm text-gray-500">{message}</p>
    <button
      onClick={onRetry}
      className={estiloDeBoton('primario', 'mt-2')}
    >
      Reintentar
    </button>
  </div>
);

// ─── Pedir herramienta form ───────────────────────────────────────────────────

const TEXTAREA_CLASS =
  "w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors resize-none placeholder-gray-400";
const INPUT_CLASS =
  "w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors placeholder-gray-400";


const PedirHerramientaForm = ({ onBack }: { onBack: () => void }) => {
  const [requestedToolName, setRequestedToolName] = useState("");
  const [almacenDestino, setAlmacenDestino] = useState("");
  const [justificacion, setJustificacion] = useState("");
  const [almacenes, setAlmacenes] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [loadingAlmacenes, setLoadingAlmacenes] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiService
      .request<{ id: string; name: string; parentId: string | null }[]>(
        "/almacen/almacenes/public/all",
      )
      .then((data) => setAlmacenes(data))
      .catch(() => {})
      .finally(() => setLoadingAlmacenes(false));
  }, []);

  const handleGuardar = async () => {
    if (!requestedToolName.trim() || !almacenDestino || !justificacion.trim()) {
      toast.error("Completa los campos obligatorios.");
      return;
    }
    setSaving(true);
    try {
      await apiService.request("/almacen/requisiciones", {
        method: "POST",
        body: JSON.stringify({
          requestedToolName: requestedToolName.trim(),
          justification: justificacion.trim(),
          destinationWarehouseId: almacenDestino,
        }),
      });
      toast.success("Requisición creada.");
      onBack();
    } catch (err: unknown) {
      toast.error(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className={estiloDeBoton('secundario', 'mt-1.5')}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Pedir herramienta
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Haz una nueva requisición de herramienta.
          </p>
        </div>
      </div>

      {/* Card */}
      <div
        className="bg-white rounded-xl border shadow-sm"
        style={{ borderColor: "#E0E0E0" }}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
          <h2 className="text-base font-semibold text-gray-800">
            Información general
          </h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Herramienta <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={requestedToolName}
                onChange={(e) => setRequestedToolName(e.target.value)}
                placeholder="Nombre de la herramienta"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Almacén destino <span className="text-red-500">*</span>
              </label>
              <SelectBuscable
                variante="formulario"
                opciones={almacenes.map((a) => ({ value: String(a.id), label: a.name }))}
                value={almacenDestino}
                onChange={setAlmacenDestino}
                disabled={loadingAlmacenes}
                placeholder={loadingAlmacenes ? "Cargando..." : "Seleccionar"}
                placeholderDeBusqueda="Buscar almacén..."
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Justificación <span className="text-red-500">*</span>
            </label>
            <textarea
              value={justificacion}
              onChange={(e) => setJustificacion(e.target.value)}
              rows={4}
              placeholder="Ej. Necesaria para mantenimiento preventivo de equipos en planta."
              className={TEXTAREA_CLASS}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onBack}
          disabled={saving}
          className={estiloDeBoton('secundario')}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleGuardar}
          disabled={saving}
          className={estiloDeBoton('primario')}
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Guardar
        </button>
      </div>
    </div>
  );
};

// ─── Shared label/value styles (mirrors HerramientaForm) ─────────────────────

const LABEL_CLASS = "block text-xs text-gray-400 mb-0.5";
const VALUE_CLASS = "text-sm text-gray-800";

// ─── ERP status badge ─────────────────────────────────────────────────────────

const ERP_STATUS_MAP: Record<
  ApiRequisicion["erpSyncStatus"],
  { color: ColorDeBadge; label: string }
> = {
  pending: { color: "amarillo", label: "Pendiente" },
  synced: { color: "verde", label: "Sincronizado" },
  failed: { color: "rojo", label: "Error" },
};

const ErpStatusBadge = ({
  status,
}: {
  status: ApiRequisicion["erpSyncStatus"];
}) => {
  const s = ERP_STATUS_MAP[status] ?? ERP_STATUS_MAP.pending;
  return <Badge color={s.color}>{s.label}</Badge>;
};

// ─── Requisición detail ───────────────────────────────────────────────────────

const RequisicionDetail = ({
  data,
  onBack,
  onMover,
}: {
  data: ApiRequisicion;
  onBack: () => void;
  onMover?: () => void;
}) => {
  const fechaDisplay = data.createdAt
    ? new Date(data.createdAt).toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: ZONA_DE_OPERACION,
      })
    : "Sin fecha";

  return (
    <div className="mx-auto px-6 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className={estiloDeBoton('secundario', 'mt-1.5')}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ver pedido</h1>
          </div>
        </div>
        {onMover && (
          <button
            type="button"
            onClick={onMover}
            className={estiloDeBoton('primario')}
          >
            Registrar herramienta
          </button>
        )}
      </div>

      {/* Información general */}
      <div
        className="bg-white rounded-xl border shadow-sm mb-6"
        style={{ borderColor: "#E0E0E0" }}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
          <h2 className="text-base font-semibold text-gray-800">
            Información general
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-4 gap-5 mb-5">
            <div>
              <p className={LABEL_CLASS}>Herramienta</p>
              <p className={VALUE_CLASS}>{data.requestedToolName}</p>
            </div>
            <div>
              <p className={LABEL_CLASS}>Almacén destino</p>
              <p className={VALUE_CLASS}>
                {data.destinationWarehouseName ?? data.destinationWarehouseId}
              </p>
            </div>
            <div>
              <p className={LABEL_CLASS}>Solicitado por</p>
              <p className={VALUE_CLASS}>
                {data.requestedByUserName ?? data.requestedByUserId}
              </p>
            </div>
            <div>
              <p className={LABEL_CLASS}>Fecha</p>
              <p className={VALUE_CLASS}>{fechaDisplay}</p>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-5 mb-5">
            <div>
              <p className={LABEL_CLASS}>Estado ERP</p>
              <ErpStatusBadge status={data.erpSyncStatus} />
            </div>
            {data.incidentReportId && (
              <div>
                <p className={LABEL_CLASS}>ID Reporte de incidente</p>
                <p className={VALUE_CLASS}>{data.incidentReportId}</p>
              </div>
            )}
          </div>
          <div>
            <p className={LABEL_CLASS}>Justificación</p>
            <p className={VALUE_CLASS}>{data.justification}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Requisicion actions menu ─────────────────────────────────────────────────

const RequisicionActionsMenu = ({ onMover }: { onMover: () => void }) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        btnRef.current &&
        !btnRef.current.closest("[data-req-menu]")?.contains(e.target as Node)
      )
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      // Menú de un solo item; abrir hacia arriba si no cabe abajo
      const estHeight = 45;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top =
        spaceBelow < estHeight + 8
          ? Math.max(8, rect.top - estHeight - 4)
          : rect.bottom + 4;
      setMenuPos({ top, left: rect.right - 176 });
    }
    setOpen((v) => !v);
  };

  return (
    <div data-req-menu="">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <MoreVertical className="w-4 h-4" style={{ color: "#315FB9" }} />
      </button>
      {open && (
        <div
          className="fixed w-max bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <button
            onClick={() => {
              onMover();
              setOpen(false);
            }}
            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Registrar herramienta
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Requisición tab ──────────────────────────────────────────────────────────

const RequisiconTab = ({
  onPedir: _onPedir,
  onVerDetalle,
  onMoverAHerramientas,
}: {
  onPedir: () => void;
  onVerDetalle: (row: ApiRequisicion) => void;
  onMoverAHerramientas: (row: ApiRequisicion) => void;
}) => {
  const [requisiciones, setRequisiciones] = useState<ApiRequisicion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const fetchRequisiciones = () => {
    setLoading(true);
    setError(null);
    apiService
      .request<{ data: ApiRequisicion[] } | ApiRequisicion[]>(
        "/almacen/requisiciones",
      )
      .then((res) => {
        const list = Array.isArray(res)
          ? res
          : ((res as { data: ApiRequisicion[] }).data ?? []);
        setRequisiciones(list);
      })
      .catch((err: unknown) => {
        const msg =
          err instanceof Error ? err.message : "Error al cargar requisiciones";
        setError(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRequisiciones();
  }, []);

  const rows = requisiciones.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      r.requestedToolName.toLowerCase().includes(q) ||
      (r.requestedByUserName ?? r.requestedByUserId).toLowerCase().includes(q);
    return matchSearch && (!filterStatus || r.erpSyncStatus === filterStatus);
  });

  // A-01: silueta de la tabla de requisiciones.
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <EsqueletoTabla filas={6} columnas={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <AlertTriangle className="w-8 h-8 text-red-400" />
        <p className="text-sm text-gray-500">{error}</p>
        <button
          onClick={fetchRequisiciones}
          className={estiloDeBoton('primario', 'mt-2')}
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
        <div className="relative w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar requisición..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent"
          />
        </div>
        {/* Estado ERP filter */}
        <SelectBuscable
          variante="listado"
          etiqueta="Estado ERP"
          todos="Todos"
          ancho="contenido"
          canDelete={false}
          opciones={[
            { value: "pending", label: "Pendiente" },
            { value: "synced", label: "Sincronizado" },
            { value: "failed", label: "Error" },
          ]}
          value={filterStatus}
          onChange={setFilterStatus}
          placeholderDeBusqueda="Buscar estado..."
        />
        <button
          onClick={() => {
            setSearch("");
            setFilterStatus("");
          }}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr
                style={{
                  backgroundColor: "#FAF9FF",
                  borderBottom: "1px solid #E4E4E4",
                }}
              >
                {[
                  "Herramienta",
                  "Almacén destino",
                  "Solicitado por",
                  "Fecha",
                  "Estado ERP",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left py-3 px-3 text-sm font-semibold first:pl-6 whitespace-nowrap"
                    style={{ color: "#534D59" }}
                  >
                    {h}
                  </th>
                ))}
                <th
                  className="hidden md:table-cell text-left py-3 px-3 text-sm font-semibold whitespace-nowrap"
                  style={{ color: "#534D59" }}
                >
                  Justificación
                </th>
                <th
                  className="text-right py-3 px-3 pr-6 text-sm font-semibold whitespace-nowrap"
                  style={{ color: "#534D59" }}
                >
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <NoResultsState />
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() => onVerDetalle(row)}
                    className="hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                    style={{ borderBottom: "1px solid #E4E4E4" }}
                  >
                    <td className="py-3 px-3 pl-6 font-medium whitespace-nowrap">
                      <span className="text-gray-700">
                        {row.requestedToolName}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                      {row.destinationWarehouseName ?? "Sin almacén"}
                    </td>
                    <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                      {row.requestedByUserName ?? row.requestedByUserId}
                    </td>
                    <td className="py-3 px-3 text-gray-500 whitespace-nowrap">
                      {new Date(row.createdAt).toLocaleDateString("es-MX", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        timeZone: ZONA_DE_OPERACION,
                      })}
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <ErpStatusBadge status={row.erpSyncStatus} />
                    </td>
                    <td className="hidden md:table-cell py-3 px-3 text-gray-500 max-w-xs truncate">
                      {row.justification}
                    </td>
                    <td
                      className="py-3 px-3 pr-6 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RequisicionActionsMenu
                        onMover={() => onMoverAHerramientas(row)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const HerramientasAlmacen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const { hasPermission, isLoading: permsLoading } = usePermissions();

  // El back deja entrar con "ver" (todas) o con "ver_asignadas" y ya acota el
  // listado; checar solo "ver" mandaba a la pantalla de acceso denegado a quien
  // tenía únicamente las asignadas, aunque el sidebar sí le mostraba el módulo.
  const canVer =
    hasPermission(ALMACEN_PERMISSIONS.HERRAMIENTAS_VER) ||
    hasPermission(ALMACEN.HERRAMIENTAS_VER_ASIGNADAS);
  const canEditar = hasPermission(ALMACEN_PERMISSIONS.HERRAMIENTAS_EDITAR);
  const canEliminar = hasPermission(ALMACEN_PERMISSIONS.HERRAMIENTAS_ELIMINAR);
  const canDarBaja = hasPermission(ALMACEN.HERRAMIENTAS_DAR_BAJA);
  const canCrearRequisicion = hasPermission(ALMACEN.REQUISICIONES_CREAR);
  // El tab vive en la URL y la requisicion abierta tiene su propia direccion:
  // antes las dos eran estado, asi que volver del detalle regresaba al tab de
  // herramientas y la pantalla no se podia compartir ni recargar.
  const [searchParams, setSearchParams] = useSearchParams();
  const { requisicionId } = useParams();
  const activeTab: "herramientas" | "requisicion" =
    searchParams.get("tab") === "requisicion" ? "requisicion" : "herramientas";
  const setActiveTab = (tab: "herramientas" | "requisicion") =>
    setSearchParams(tab === "requisicion" ? { tab } : {}, { replace: true });

  const [showPedirForm, setShowPedirForm] = useState(false);
  const requisicionDelState =
    (location.state as { preselectRequisicion?: ApiRequisicion } | null)
      ?.preselectRequisicion ?? null;
  const [requisicionPorUrl, setRequisicionPorUrl] =
    useState<ApiRequisicion | null>(requisicionDelState);

  // Si la pantalla se abre directo por URL no hay nada en el state: se busca la
  // requisicion en el listado.
  useEffect(() => {
    if (!requisicionId || requisicionPorUrl?.id === requisicionId) return;
    apiService
      .request<{ data: ApiRequisicion[] } | ApiRequisicion[]>(
        "/almacen/requisiciones",
      )
      .then((res) => {
        const list = Array.isArray(res)
          ? res
          : ((res as { data: ApiRequisicion[] }).data ?? []);
        setRequisicionPorUrl(list.find((r) => r.id === requisicionId) ?? null);
      })
      .catch(() => setRequisicionPorUrl(null));
  }, [requisicionId, requisicionPorUrl]);

  const selectedRequisicion = requisicionId ? requisicionPorUrl : null;
  const { claseDeFila, animarSalida, cancelarSalida } = useFilaSaliente();
  const [herramientas, setHerramientas] = useState<Herramienta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    nombre: string;
    currentMovementId: string | null;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reportarDanoTool, setReportarDanoTool] = useState<{
    id: string;
    nombre: string;
  } | null>(null);
  const [darBajaTool, setDarBajaTool] = useState<{
    id: string;
    nombre: string;
  } | null>(null);
  const [darAltaTool, setDarAltaTool] = useState<{
    id: string;
    nombre: string;
  } | null>(null);
  // Los filtros van en la URL y no en useState: al volver del detalle de una
  // herramienta la pantalla se monta de nuevo y en memoria se perdian. Cambiar
  // de tab si los limpia, y esta bien: el otro tab filtra por otras cosas.
  const [search, setSearch] = useEstadoEnUrl<string>("buscar", "");
  const [filterAlmacen, setFilterAlmacen] = useEstadoEnUrl<string>("almacen", "");
  const [filterCategoria, setFilterCategoria] = useEstadoEnUrl<string>("categoria", "");
  const [filterProyecto, setFilterProyecto] = useEstadoEnUrl<string>("proyecto", "");
  const [filterResponsable, setFilterResponsable] = useEstadoEnUrl<string>("responsable", "");
  const [filterEstado, setFilterEstado] = useEstadoEnUrl<string>("estado", "");
  const [filterDisponibilidad, setFilterDisponibilidad] = useEstadoEnUrl<string>("disponibilidad", "");

  const fetchHerramientas = () => {
    setLoading(true);
    setError(null);
    // Las herramientas dadas de baja se excluyen por defecto; solo se traen
    // cuando el filtro de estado es "Dada de baja" (el back respeta ?status).
    const params =
      filterEstado === "Dada de baja"
        ? { limit: 100, status: "decommissioned" as const }
        : { limit: 100 };
    herramientasApi
      .findAll(params)
      .then((paginated) => {
        setHerramientas(
          [...paginated.data]
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime(),
            )
            .map(mapToolDto),
        );
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Error desconocido";
        setError(msg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Refetch al entrar/salir del filtro "Dada de baja": cambia la fuente en el back
  useEffect(() => {
    fetchHerramientas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEstado === "Dada de baja"]);

  const handleLimpiar = () => {
    setSearch("");
    setFilterAlmacen("");
    setFilterCategoria("");
    setFilterProyecto("");
    setFilterResponsable("");
    setFilterEstado("");
    setFilterDisponibilidad("");
  };

  // Derived filter options from fetched data
  const uniqueAlmacenes = [...new Set(herramientas.map((h) => h.almacen))];
  const uniqueCategorias = [...new Set(herramientas.map((h) => h.categoria))];
  const uniqueProyectos = [
    ...new Set(herramientas.map((h) => h.proyectoAsignado)),
  ];
  const uniqueResponsables = [
    ...new Set(herramientas.map((h) => h.responsable)),
  ];

  const rows = herramientas.filter((h) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      h.nombre.toLowerCase().includes(q) ||
      h.codigoSerie.toLowerCase().includes(q) ||
      h.internalCode.toLowerCase().includes(q) ||
      h.responsable.toLowerCase().includes(q);
    return (
      matchSearch &&
      (!filterAlmacen || h.almacen === filterAlmacen) &&
      (!filterCategoria || h.categoria === filterCategoria) &&
      (!filterProyecto || h.proyectoAsignado === filterProyecto) &&
      (!filterResponsable || h.responsable === filterResponsable) &&
      (!filterEstado || h.estado === filterEstado) &&
      (!filterDisponibilidad || h.disponibilidad === filterDisponibilidad)
    );
  });

  // Herramientas activas en el almacén seleccionado (sólo cuando hay filtro)
  const activasCount = useMemo(
    () =>
      filterAlmacen
        ? herramientas.filter(
            (h) =>
              h.almacen === filterAlmacen && h.disponibilidad === "Disponible",
          ).length
        : null,
    [herramientas, filterAlmacen],
  );

  // Mientras cargan los permisos, mostrar loader (evita el flash de la pantalla
  // de "sin permisos" antes de que se resuelvan los permisos del usuario).
  if (permsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#253579]" />
      </div>
    );
  }

  if (!canVer) return <PermissionsDenyPage />;

  const handleEliminar = async () => {
    if (!confirmDelete || deleting) return;
    const id = confirmDelete.id;
    setDeleting(true);
    try {
      await apiService.request(`/almacen/herramientas/${id}`, {
        method: "DELETE",
      });
      setConfirmDelete(null);
      // A-16: la fila sale y hasta entonces se relee el listado. Al reves
      // desaparece de golpe y la animacion nunca se ve.
      await animarSalida(id);
      fetchHerramientas();
    } catch (err: unknown) {
      cancelarSalida(id);
      toast.error(friendlyError(err));
    } finally {
      setDeleting(false);
    }
  };

  // Sub-routes handled via URL
  if (pathname === "/almacen/registro/crear") {
    return (
      <HerramientaForm
        mode="create"
        onBack={() => {
          navigate("/almacen/registro");
          fetchHerramientas();
        }}
      />
    );
  }
  if (selectedRequisicion) {
    return (
      <RequisicionDetail
        data={selectedRequisicion}
        onBack={() => {
          if (location.key !== "default") navigate(-1);
          else navigate("/almacen/registro?tab=requisicion");
        }}
        onMover={() =>
          navigate("/almacen/registro/crear", {
            state: {
              prefill: {
                nombre: selectedRequisicion.requestedToolName,
                almacen: selectedRequisicion.destinationWarehouseId,
              },
            },
          })
        }
      />
    );
  }

  if (showPedirForm) {
    return <PedirHerramientaForm onBack={() => setShowPedirForm(false)} />;
  }

  return (
    <div className="mx-auto px-8 py-8">
      {/* Reportar daño modal */}
      {reportarDanoTool && (
        <ReporteModal
          toolId={reportarDanoTool.id}
          toolName={reportarDanoTool.nombre}
          onClose={() => setReportarDanoTool(null)}
          onCreated={(reportId) => navigate(`/almacen/reportes/${reportId}/ver`)}
        />
      )}

      {/* Dar de baja modal */}
      {darBajaTool && (
        <DarBajaModal
          toolId={darBajaTool.id}
          toolName={darBajaTool.nombre}
          onClose={() => setDarBajaTool(null)}
          onDone={() => {
            setDarBajaTool(null);
            fetchHerramientas();
          }}
        />
      )}

      {/* Dar de alta (reactivar) modal */}
      {darAltaTool && (
        <DarAltaModal
          toolId={darAltaTool.id}
          toolName={darAltaTool.nombre}
          onClose={() => setDarAltaTool(null)}
          onDone={() => {
            setDarAltaTool(null);
            fetchHerramientas();
          }}
        />
      )}

      {/* Confirm delete modal */}
      {confirmDelete && (
        <CapaDeModal>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              {confirmDelete.currentMovementId
                ? "Devuelve la herramienta primero"
                : "¿Eliminar herramienta?"}
            </h3>
            {confirmDelete.currentMovementId ? (
              <p className="text-sm text-gray-500 mb-5">
                <span className="font-medium text-gray-700">
                  "{confirmDelete.nombre}"
                </span>{" "}
                tiene una asignación activa. Debes registrar su devolución antes
                de poder eliminarla.
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-5">
                Esta acción dará de baja{" "}
                <span className="font-medium text-gray-700">
                  "{confirmDelete.nombre}"
                </span>
                . No se puede deshacer.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              {confirmDelete.currentMovementId ? (
                <button
                  type="button"
                  onClick={() => {
                    const toolId = confirmDelete.id;
                    setConfirmDelete(null);
                    navigate(`/almacen/asignacion/${toolId}/devolver`);
                  }}
                  className={estiloDeBoton('primario')}
                >
                  Devolver
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleEliminar}
                  disabled={deleting}
                  className={estiloDeBoton('peligro')}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Eliminando...
                    </>
                  ) : (
                    "Sí, eliminar"
                  )}
                </button>
              )}
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Herramientas
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Busca y consulta todas las herramientas registradas en el almacén
          </p>
        </div>
        {activeTab === "herramientas" ? (
          <PermissionGuard permission={ALMACEN_PERMISSIONS.HERRAMIENTAS_CREAR}>
            <button
              onClick={() => navigate("/almacen/registro/crear")}
              className={estiloDeBoton('primario')}
            >
              <CirclePlus className="w-4 h-4" />
              Nueva herramienta
            </button>
          </PermissionGuard>
        ) : canCrearRequisicion ? (
          <button
            onClick={() => setShowPedirForm(true)}
            className={estiloDeBoton('primario')}
          >
            <CirclePlus className="w-4 h-4" />
            Pedir herramienta
          </button>
        ) : null}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6">
        {(["herramientas", "requisicion"] as const).map((tab) => {
          const label =
            tab === "herramientas"
              ? "Herramientas"
              : "Requisición de herramientas";
          const isActive = activeTab === tab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm transition-colors border-b-2 ${
                isActive
                  ? "border-[#477FFA] font-semibold"
                  : "border-transparent hover:border-gray-200"
              }`}
              style={{ color: "#1E1E1F" }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {activeTab === "requisicion" ? (
        <RequisiconTab
          onPedir={() => setShowPedirForm(true)}
          onVerDetalle={(row) =>
            navigate(`/almacen/registro/requisicion/${row.id}`, {
              state: { preselectRequisicion: row },
            })
          }
          onMoverAHerramientas={(row) => {
            navigate("/almacen/registro/crear", {
              state: {
                prefill: {
                  nombre: row.requestedToolName,
                  almacen: row.destinationWarehouseId,
                },
              },
            });
          }}
        />
      ) : loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchHerramientas} />
      ) : herramientas.length === 0 ? (
        <NoDataState />
      ) : (
        <div className="contenido-listo">
          {/* Filter bar */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
            <div className="relative w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en herramientas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent"
              />
            </div>
            <FilterSelect
              label="Almacén"
              options={(uniqueAlmacenes).map((o) => ({ value: o, label: o }))}
              value={filterAlmacen}
              onChange={setFilterAlmacen}
            ancho="contenido"
          />
            <FilterSelect
              label="Categoría"
              options={(uniqueCategorias).map((o) => ({ value: o, label: o }))}
              value={filterCategoria}
              onChange={setFilterCategoria}
            ancho="contenido"
          />
            <FilterSelect
              label="Proyecto asignado"
              options={(uniqueProyectos).map((o) => ({ value: o, label: o }))}
              value={filterProyecto}
              onChange={setFilterProyecto}
            ancho="contenido"
          />
            <FilterSelect
              label="Responsable"
              options={(uniqueResponsables).map((o) => ({ value: o, label: o }))}
              value={filterResponsable}
              onChange={setFilterResponsable}
            ancho="contenido"
          />
            <FilterSelect
              label="Estado"
              options={([
                "En buen estado",
                "Equipo nuevo",
                "Falla/daño",
                "Faltante",
                "En mantenimiento",
                "Dada de baja",
              ]).map((o) => ({ value: o, label: o }))}
              value={filterEstado}
              onChange={setFilterEstado}
            ancho="contenido"
          />
            <FilterSelect
              label="Disponibilidad"
              options={(["Disponible", "No disponible"]).map((o) => ({ value: o, label: o }))}
              value={filterDisponibilidad}
              onChange={setFilterDisponibilidad}
            ancho="contenido"
          />
            <button
              onClick={handleLimpiar}
              className="text-sm font-semibold text-[#315FB9] hover:underline"
            >
              Limpiar
            </button>
            {/* Contador de herramientas activas por almacén */}
            {activasCount !== null && (
              <span className="ml-auto text-sm text-[#315FB9] bg-[#EDFAFE] px-3 py-1 rounded">
                <span className="text-[#315FB9]">{activasCount}</span>{" "}
                herramienta{activasCount !== 1 ? "s" : ""} activa
                {activasCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr
                    style={{
                      backgroundColor: "#FAF9FF",
                      borderBottom: "1px solid #E4E4E4",
                    }}
                  >
                    {[
                      "Herramienta",
                      "Almacén",
                      "Fecha y hora de alta",
                      "Categoría",
                      "Proyecto asignado",
                      "Responsable",
                      "Número de serie",
                      "Código interno",
                      "Disponibilidad",
                      "Estado",
                      "Acciones",
                    ].map((h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-3 text-sm font-semibold first:pl-6 last:pr-4 last:text-right whitespace-nowrap"
                        style={{ color: "#534D59" }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={11}>
                        <NoResultsState />
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => navigate(`/almacen/registro/${row.id}`)}
                        className={`hover:bg-[#F4F7FB] transition-colors cursor-pointer ${claseDeFila(row.id)}`}
                        style={{ borderBottom: "1px solid #E4E4E4" }}
                      >
                        <td className="py-3 px-3 pl-6 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <MiniaturaHerramienta
                              foto={row.foto}
                              alt={row.nombre}
                            />
                            <span className="text-gray-700 font-medium">
                              {row.nombre}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          {row.almacen}
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          {row.createdAt
                            ? new Date(row.createdAt).toLocaleString("es-MX", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                timeZone: ZONA_DE_OPERACION,
                              })
                            : "Sin fecha"}
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          {row.categoria}
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          {row.proyectoAsignado}
                        </td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">
                          <ResponsableDeHerramienta
                            nombre={row.responsable}
                            pendienteDe={row.pendienteDe}
                            estadoDeAprobacion={row.estadoDeAprobacion}
                            compacto
                          />
                        </td>
                        <td className="py-3 px-3 text-gray-500 whitespace-nowrap">
                          {row.codigoSerie}
                        </td>
                        <td className="py-3 px-3 text-gray-500 whitespace-nowrap">
                          {row.internalCode}
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <DisponibilidadBadge value={row.disponibilidad} />
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <EstadoBadge estado={row.estado} />
                        </td>
                        <td
                          className="py-3 px-3 pr-4 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionsMenu
                            onVer={() =>
                              navigate(`/almacen/registro/${row.id}`)
                            }
                            onEditar={
                              canEditar
                                ? () =>
                                    navigate(
                                      `/almacen/registro/${row.id}/editar`,
                                    )
                                : undefined
                            }
                            onHistorial={() =>
                              navigate(`/almacen/historial/herramientas/${row.id}`, {
                                state: {
                                  preselectTool: {
                                    id: row.id,
                                    nombre: row.nombre,
                                    codigo:
                                      row.codigoSerie === "Sin número de serie"
                                        ? ""
                                        : row.codigoSerie,
                                    internalCode:
                                      row.internalCode === "Sin código interno"
                                        ? ""
                                        : row.internalCode,
                                    fecha: "",
                                    actividad: "Reciente",
                                    // Iba fijo en null, asi que entrar al
                                    // historial desde aqui mostraba la
                                    // herramienta sin foto, y llegando por
                                    // otro lado si salia.
                                    photoUrl: row.foto,
                                  },
                                },
                              })
                            }
                            onReportarDano={() =>
                              setReportarDanoTool({
                                id: row.id,
                                nombre: row.nombre,
                              })
                            }
                            onDarBaja={
                              canDarBaja && row.estado !== "Dada de baja"
                                ? () =>
                                    setDarBajaTool({
                                      id: row.id,
                                      nombre: row.nombre,
                                    })
                                : undefined
                            }
                            onDarAlta={
                              canDarBaja && row.estado === "Dada de baja"
                                ? () =>
                                    setDarAltaTool({
                                      id: row.id,
                                      nombre: row.nombre,
                                    })
                                : undefined
                            }
                            onEliminar={
                              canEliminar
                                ? () =>
                                    setConfirmDelete({
                                      id: row.id,
                                      nombre: row.nombre,
                                      currentMovementId: row.currentMovementId,
                                    })
                                : undefined
                            }
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerramientasAlmacen;
